import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  TrendingUp, Wallet, FileWarning, CreditCard, Users, Loader2,
  Building2, AlertTriangle, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Franchise-Umsatz-Cockpit für Admin-Dashboard.
 *
 * Zeigt pro Studio:
 * - Umsatz (window-basiert)
 * - Offene Rechnungen (Anzahl + Summe, davon overdue)
 * - Aktive Abos + geschätzter MRR
 * - Hinterlegte Kreditkarten
 * - Kunden-Count
 * - Payrexx-Routing-Indikator (Hauptkonto / Eigenes Studio-Konto)
 */
const WINDOW_LABELS = {
  today: 'Heute',
  week: 'Woche',
  month: 'Monat',
  year: 'Jahr',
};

const fmtCHF = (n) =>
  new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

const fmtCHFexact = (n) =>
  new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(n || 0);

const StudioRevenueCard = () => {
  const navigate = useNavigate();
  const [window, setWindow] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/studios/revenue`, {
        params: { window },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const studios = useMemo(() => data?.studios || [], [data]);
  const overall = data?.overall;

  // Sortierung: Studios mit Umsatz zuerst, dann Standard-Studio
  const sortedStudios = useMemo(() => {
    return [...studios].sort((a, b) => {
      if (a.revenue !== b.revenue) return b.revenue - a.revenue;
      if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
      return (a.studio_name || '').localeCompare(b.studio_name || '');
    });
  }, [studios]);

  // Hide card if no studios
  if (!loading && studios.length < 1) return null;

  const overdueWarn = overall?.overdue_count > 0;

  return (
    <Card className="border-emerald-100" data-testid="studio-revenue-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <Wallet className="h-5 w-5 text-emerald-600" />
            Franchise-Umsatz
            <span className="text-xs font-normal text-muted-foreground">
              ({WINDOW_LABELS[window]})
            </span>
          </CardTitle>
          <div className="flex items-center gap-1">
            {Object.entries(WINDOW_LABELS).map(([key, label]) => (
              <Button
                key={key}
                variant={window === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setWindow(key)}
                className="h-7 px-2 text-xs"
                data-testid={`revenue-toggle-${key}`}
              >
                {label}
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchData(true)}
              className="h-7 w-7 p-0"
              disabled={refreshing}
              data-testid="revenue-refresh"
              title="Aktualisieren"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> lädt...
          </div>
        ) : !data ? (
          <div className="text-sm text-muted-foreground italic py-4">
            Keine Daten verfügbar.
          </div>
        ) : (
          <>
            {/* Gesamt-KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="revenue-overall">
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2.5">
                <div className="flex items-center gap-1 text-[11px] text-emerald-700 font-medium">
                  <TrendingUp className="h-3 w-3" /> Umsatz
                </div>
                <div className="text-lg font-bold text-emerald-800 mt-0.5">
                  {fmtCHF(overall.revenue)}
                </div>
                <div className="text-[10px] text-emerald-600/80 mt-0.5">
                  {overall.paid_invoices_count} bezahlte Rg.
                </div>
              </div>

              <div className={`rounded-lg p-2.5 border ${overdueWarn ? 'bg-rose-50 border-rose-100' : 'bg-amber-50 border-amber-100'}`}>
                <div className={`flex items-center gap-1 text-[11px] font-medium ${overdueWarn ? 'text-rose-700' : 'text-amber-700'}`}>
                  <FileWarning className="h-3 w-3" /> Offen
                </div>
                <div className={`text-lg font-bold mt-0.5 ${overdueWarn ? 'text-rose-800' : 'text-amber-800'}`}>
                  {fmtCHF(overall.open_invoices_total)}
                </div>
                <div className={`text-[10px] mt-0.5 ${overdueWarn ? 'text-rose-600/80' : 'text-amber-600/80'} flex items-center gap-1`}>
                  {overall.open_invoices_count} Rg.
                  {overdueWarn && (
                    <>
                      <span>·</span>
                      <AlertTriangle className="h-2.5 w-2.5" />
                      {overall.overdue_count} überfällig
                    </>
                  )}
                </div>
              </div>

              <div className="rounded-lg bg-sky-50 border border-sky-100 p-2.5">
                <div className="flex items-center gap-1 text-[11px] text-sky-700 font-medium">
                  <RefreshCw className="h-3 w-3" /> MRR
                </div>
                <div className="text-lg font-bold text-sky-800 mt-0.5">
                  {fmtCHF(overall.monthly_recurring_revenue)}
                </div>
                <div className="text-[10px] text-sky-600/80 mt-0.5">
                  {overall.active_subscriptions} aktive Abos
                </div>
              </div>

              <div className="rounded-lg bg-violet-50 border border-violet-100 p-2.5">
                <div className="flex items-center gap-1 text-[11px] text-violet-700 font-medium">
                  <CreditCard className="h-3 w-3" /> Karten
                </div>
                <div className="text-lg font-bold text-violet-800 mt-0.5">
                  {overall.stored_cards}
                </div>
                <div className="text-[10px] text-violet-600/80 mt-0.5">
                  von {overall.customer_count} Kunden
                </div>
              </div>
            </div>

            {/* Pro Studio Detail-Liste */}
            <div className="space-y-2 pt-2 border-t">
              {sortedStudios.map((s) => {
                const sharePct = overall.revenue > 0
                  ? Math.round((s.revenue / overall.revenue) * 100)
                  : 0;
                return (
                  <div
                    key={s.studio_id}
                    className="rounded-md border border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/30 transition cursor-pointer p-2.5"
                    onClick={() => navigate(`/finanzen/rechnungen`)}
                    data-testid={`revenue-studio-${s.studio_id}`}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                        <Building2 className="h-3.5 w-3.5 text-slate-500" />
                        {s.studio_name}
                        {s.is_default && (
                          <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-teal-300 text-teal-700">
                            Standard
                          </Badge>
                        )}
                        {s.payrexx_enabled ? (
                          <Badge className="text-[10px] py-0 px-1.5 bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0">
                            Studio-Payrexx{s.payrexx_instance ? ` · ${s.payrexx_instance}` : ''}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] py-0 px-1.5 text-slate-500">
                            Hauptkonto
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-emerald-700">
                          {fmtCHFexact(s.revenue)}
                        </div>
                        <div className="text-[10px] text-slate-500">
                          {sharePct}% Anteil
                        </div>
                      </div>
                    </div>

                    {/* Anteils-Balken */}
                    <div className="h-1 bg-slate-100 rounded-full overflow-hidden mb-2">
                      <div
                        className="h-full bg-emerald-500 transition-all duration-700"
                        style={{ width: `${sharePct}%` }}
                      />
                    </div>

                    {/* Detail-Zeile */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[11px]">
                      <div className="text-slate-600">
                        <FileWarning className="inline h-3 w-3 mr-1 text-amber-600" />
                        <span className="font-medium">{s.open_invoices_count}</span> offen
                        {s.open_invoices_total > 0 && (
                          <span className="text-slate-400"> ({fmtCHF(s.open_invoices_total)})</span>
                        )}
                        {s.overdue_count > 0 && (
                          <span className="text-rose-600 font-medium ml-1">
                            · {s.overdue_count} fällig
                          </span>
                        )}
                      </div>
                      <div className="text-slate-600">
                        <RefreshCw className="inline h-3 w-3 mr-1 text-sky-600" />
                        <span className="font-medium">{s.active_subscriptions}</span> Abos
                        {s.monthly_recurring_revenue > 0 && (
                          <span className="text-slate-400"> ({fmtCHF(s.monthly_recurring_revenue)}/Mt)</span>
                        )}
                      </div>
                      <div className="text-slate-600">
                        <CreditCard className="inline h-3 w-3 mr-1 text-violet-600" />
                        <span className="font-medium">{s.stored_cards}</span> Karten
                      </div>
                      <div className="text-slate-600">
                        <Users className="inline h-3 w-3 mr-1 text-slate-500" />
                        <span className="font-medium">{s.customer_count}</span> Kunden
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Hint */}
            <div className="text-[10px] text-slate-400 italic pt-1">
              Umsatz basiert auf bezahlten Rechnungen mit <code>paid_at</code> im
              gewählten Zeitfenster. MRR = monatliche Approximation aller aktiven Abos.
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StudioRevenueCard;
